import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import type { RootState } from "../../../store/store";
import { DpCostCenter, listCostCentersApi } from "../../dp/api/settingsApi";
import { createStoreApi, StorePublic, updateStoreByIdApi } from "../storeApi";

/**
 * UFs oferecidas no cadastro — as mesmas dos modais antigos (a operação atende
 * RJ e SP). Uma loja gravada com outra UF continua mostrando a sua.
 */
const UF_OPCOES = ["RJ", "SP"];

type Props = {
  /** `null` = cadastro de loja nova. */
  store: StorePublic | null;
  /** O `id` do `<form>`: o botão de salvar pode ficar fora dele (rodapé do painel). */
  formId: string;
  /** Campos travados: loja desativada ou quem não é ADMIN (a API só deixa o ADMIN gravar). */
  readOnly?: boolean;
  autoFocus?: boolean;
  onSavingChange?: (saving: boolean) => void;
  /** Avisa se dá para salvar (nome preenchido e não travado). */
  onValidChange?: (valid: boolean) => void;
  onSaved: (store: StorePublic, mode: "create" | "edit") => void;
};

const errorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

/**
 * Nome fantasia, endereço, UF e centros de custo da loja. Usado no painel de
 * "Nova loja" e na aba Dados da ficha da loja; o botão de salvar é de quem usa.
 */
const StoreDataForm: React.FC<Props> = ({ store, formId, readOnly, autoFocus, onSavingChange, onValidChange, onSaved }) => {
  const [nomeFantasia, setNomeFantasia] = useState(store?.nomeFantasia ?? "");
  const [endereco, setEndereco] = useState(store?.endereco ?? "");
  const [estado, setEstado] = useState(store?.estado ?? "");
  const [saving, setSaving] = useState(false);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  const token = useSelector((state: RootState) => state.auth.accessToken);
  /** `null` enquanto carrega ou se a lista falhou: aí o salvar não mexe nos vínculos. */
  const [costCenters, setCostCenters] = useState<DpCostCenter[] | null>(null);
  const [costCentersError, setCostCentersError] = useState(false);
  const [costCenterIds, setCostCenterIds] = useState<string[]>([]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    // Inclui os inativos para não perder um vínculo já existente ao salvar.
    listCostCentersApi(token, true)
      .then((list) => {
        if (cancelled) return;
        const linked = store ? list.filter((c) => c.storeId === store.id).map((c) => c.id) : [];
        setCostCenters(list);
        setCostCenterIds(linked);
      })
      .catch(() => {
        if (!cancelled) setCostCentersError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [token, store]);

  useEffect(() => {
    if (autoFocus) firstFieldRef.current?.focus();
  }, [autoFocus]);

  /** Ativos, mais os inativos que já estão vinculados à loja. */
  const costCenterOptions = useMemo(
    () => (costCenters ?? []).filter((c) => c.isActive || costCenterIds.includes(c.id)),
    [costCenters, costCenterIds],
  );

  const toggleCostCenter = (id: string, checked: boolean) =>
    setCostCenterIds((ids) => (checked ? [...ids, id] : ids.filter((x) => x !== id)));

  const ufOptions = useMemo(
    () => (estado && !UF_OPCOES.includes(estado) ? [...UF_OPCOES, estado] : UF_OPCOES),
    [estado],
  );

  const locked = saving || !!readOnly;
  const valid = nomeFantasia.trim().length > 0 && !locked;

  useEffect(() => {
    onValidChange?.(valid);
  }, [valid, onValidChange]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;

    const dto = {
      nomeFantasia: nomeFantasia.trim(),
      endereco: endereco.trim() || undefined,
      estado: estado || undefined,
      ...(costCenters ? { costCenterIds } : {}),
    };

    try {
      setSaving(true);
      onSavingChange?.(true);
      if (store) {
        const updated = await updateStoreByIdApi(store.id, dto);
        toast.success("Loja atualizada com sucesso!");
        onSaved(updated, "edit");
      } else {
        const created = await createStoreApi(dto);
        toast.success("Loja cadastrada com sucesso!");
        onSaved(created, "create");
      }
    } catch (error) {
      toast.error(errorMessage(error, store ? "Erro ao atualizar loja." : "Erro ao cadastrar loja."));
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  };

  return (
    <form id={formId} className="storePanel__body storeData" onSubmit={handleSubmit}>
      <div className="ecoField">
        <label htmlFor={`${formId}-nome`}>Nome fantasia</label>
        <input
          ref={firstFieldRef}
          id={`${formId}-nome`}
          className="ecoInput"
          value={nomeFantasia}
          onChange={(e) => setNomeFantasia(e.target.value)}
          placeholder="Ex.: Loja Centro"
          disabled={locked}
          autoComplete="off"
        />
      </div>

      <div className="ecoField">
        <label htmlFor={`${formId}-endereco`}>
          Endereço <span className="ecoLabel__optional">(opcional)</span>
        </label>
        <input
          id={`${formId}-endereco`}
          className="ecoInput"
          value={endereco}
          onChange={(e) => setEndereco(e.target.value)}
          placeholder="Ex.: Rua Exemplo, 123"
          disabled={locked}
          autoComplete="off"
        />
      </div>

      <div className="ecoField storePanel__uf">
        <label htmlFor={`${formId}-uf`}>
          UF <span className="ecoLabel__optional">(opcional)</span>
        </label>
        <select
          id={`${formId}-uf`}
          className="ecoSelect"
          value={estado}
          onChange={(e) => setEstado(e.target.value)}
          disabled={locked}
        >
          <option value="">Selecione...</option>
          {ufOptions.map((uf) => (
            <option key={uf} value={uf}>
              {uf}
            </option>
          ))}
        </select>
      </div>

      <fieldset className="ecoField storePanel__costCenters">
        <legend>
          Centros de custo <span className="ecoLabel__optional">(opcional)</span>
        </legend>
        {costCentersError ? (
          <span className="storePanel__fieldHint">
            Não foi possível carregar os centros de custo. Os vínculos atuais não serão alterados.
          </span>
        ) : costCenters === null ? (
          <span className="storePanel__fieldHint">Carregando...</span>
        ) : costCenterOptions.length === 0 ? (
          <span className="storePanel__fieldHint">
            Nenhum centro de custo cadastrado. Cadastre em Departamento Pessoal, Cadastros.
          </span>
        ) : (
          <div className="storePanel__checkList">
            {costCenterOptions.map((c) => {
              // O centro de custo tem uma loja só (ADR 0041): o de outra loja não se marca aqui.
              const otherStore = c.storeId && c.storeId !== store?.id ? c.store?.nomeFantasia ?? "outra loja" : null;
              return (
                <label key={c.id} className="storePanel__check">
                  <input
                    type="checkbox"
                    checked={costCenterIds.includes(c.id)}
                    onChange={(e) => toggleCostCenter(c.id, e.target.checked)}
                    disabled={locked || !!otherStore}
                  />
                  <span>
                    {c.code} · {c.name}
                    {c.isActive ? null : <span className="storePanel__checkTag">inativo</span>}
                    {otherStore ? <span className="storePanel__checkTag">da loja {otherStore}</span> : null}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </fieldset>
    </form>
  );
};

export default StoreDataForm;
